import type { MaybeRef, RemovableRef } from "@vueuse/shared";

import { COMMERCE_CATEGORY } from "~/constants";
import useUserStore from "~/stores/user";

export default function useEventLock(options: {
  commerceCoverage: MaybeRef<TCommerceCategory[]>;
  isEventAvailableInUserRegion: MaybeRef<boolean | undefined>;
  isEventAvailableForUser: MaybeRef<boolean | undefined>;
}) {
  const { t } = useI18n();
  const userStore = useUserStore();

  const commerceCategoryCoverage: RemovableRef<TCommerceCategory[]> = resolveRef(options.commerceCoverage);
  const isEventAvailableInUserRegion: RemovableRef<boolean | undefined> = resolveRef(options.isEventAvailableInUserRegion);
  const isEventAvailableForUser: RemovableRef<boolean | undefined> = resolveRef(options.isEventAvailableForUser);

  const isLoggedIn = computed(() => Boolean(userStore.user));
  const commerceCategory = computed(() => getCommerceCategory(commerceCategoryCoverage.value));

  const isAuthLock = computed(() => (commerceCategory.value === COMMERCE_CATEGORY.MEMBER
    || commerceCategory.value === COMMERCE_CATEGORY.SUBSCRIPTION
    || commerceCategory.value === COMMERCE_CATEGORY.PPV
  ) && !isLoggedIn.value);

  const isPremiumLock = computed(() => commerceCategory.value === COMMERCE_CATEGORY.SUBSCRIPTION || commerceCategory.value === COMMERCE_CATEGORY.PPV);

  const isGeoBlocked = computed(() => !isEventAvailableInUserRegion.value);
  const isUserBlocked = computed(() => !isEventAvailableForUser.value);
  const isLocked = computed(() => isGeoBlocked.value || isUserBlocked.value);

  const lockMessage = computed(() => {
    if (isAuthLock.value)
      return t("labels.lock_event_message.member");
    if (isGeoBlocked.value)
      return t("labels.lock_event_message.geo_blocking");
    if (isPremiumLock.value)
      return t("labels.lock_event_message.premium");

    return "";
  });

  return { lockMessage, isAuthLock, isLocked, isGeoBlocked };
}
